import React from "react";
import {useDispatch, useSelector} from "react-redux";
import useAzureService from "../../../services/azure";
import useJiraService from "../../../services/jira";
import {User, ImportStep} from "../../../types/manageUsers";
import {setImportStep, removeImportedUsers} from "../../../redux/slices/manageUsers";
import {clearAzureSyncDifferences} from "../../../redux/slices/azureTeams";
import {HiOutlineUserGroup, HiX} from "react-icons/hi";
import CustomButton from "../../../components/common/CustomButton";
import {IntegrationActiveType} from "../../Settings/types";
import Portal from "../../../components/Portal";
import {useResourcesService} from "../../../services";
import {useJwt} from "../../../helpers/common";
import {azureTeamSyncRepository} from "../../../services/repositories";

interface ConfirmSyncTeamsModalProps {
  activeTab: IntegrationActiveType | null;
  setShowImportConfirm: (showImport: boolean) => void;
  hasChangesToImport: boolean;
}

const SyncUsersModal: React.FC<ConfirmSyncTeamsModalProps> = ({activeTab, setShowImportConfirm, hasChangesToImport}) => {
  const dispatch = useDispatch();

  const {
    users,
    initialUsers,
    importLoading: importUsersLoading,
    userEmails,
  } = useSelector((state: any) => state.manageUsers);

  const { importAzureUsers, fetchAzureUsers, fetchAzureTeamSyncDifferences } = useAzureService();
  const { importJiraUsers, fetchJiraUsers } = useJiraService();
  const { fetchManagerResources } = useResourcesService();
  const jwtData: any = useJwt();

  const getChangedUsers = () => {
    if (initialUsers.length === 0) return { added: [], removed: [] };

    // Compare current selection with initial selection
    const initialSelectionMap = new Map(initialUsers.map((user: User) => [user.id, user.selected]));

    const added = users.filter((user: User) => {
      const initialSelected = initialSelectionMap.get(user.id) || false;
      return user.selected && !initialSelected;
    });

    const removed = users.filter((user: User) => {
      const initialSelected = initialSelectionMap.get(user.id) || false;
      return !user.selected && initialSelected;
    });

    return { added, removed };
  };

  const confirmImport = async () => {
    const selectedUsers = users.filter((user: User) => user.selected).map((user: User) => (
      (userEmails[user.id] && userEmails[user.id].trim() !== '')
        ? { ...user, email: userEmails[user.id] }
        : user
    ));

    try {
      if(activeTab === IntegrationActiveType.AZURE) {
        dispatch(clearAzureSyncDifferences());
      }
      
      if(activeTab === IntegrationActiveType.AZURE) {
        await importAzureUsers(selectedUsers);
        
        // Close modal immediately after successful import
        setShowImportConfirm(false);
        
        // Remove imported users from Redux state (no API call needed)
        dispatch(removeImportedUsers(selectedUsers.map((u: User) => u.id)));
        
        // Reset import step
        dispatch(setImportStep(ImportStep.USER_SELECTION));
        
        // Refresh other data in background
        if(jwtData.org_id) {
          await fetchManagerResources(jwtData.org_id);
        }
        if(jwtData.org_id && jwtData.user_id) {
          await azureTeamSyncRepository.clearCachedData(jwtData.org_id, jwtData.user_id);
          await fetchAzureTeamSyncDifferences(jwtData.org_id, jwtData.user_id);
        }
      } else if(activeTab === IntegrationActiveType.JIRA) {
        await importJiraUsers(selectedUsers);
        
        // Close modal immediately after successful import
        setShowImportConfirm(false);
        
        // Remove imported users from Redux state (no API call needed)
        dispatch(removeImportedUsers(selectedUsers.map((u: User) => u.id)));
        
        // Reset import step
        dispatch(setImportStep(ImportStep.USER_SELECTION));
      }
    } catch (error) {
      console.error((error as any).message);
      setShowImportConfirm(false);
    }
  };

  return (
    <Portal>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4 py-6">
        <button
          type="button"
          aria-label="Close import users modal"
          className="absolute inset-0 w-full h-full bg-black/50 backdrop-blur-sm cursor-default z-10"
          onClick={() => setShowImportConfirm(false)}
        />
        <dialog
          open
          className="z-20 bg-secondary-light dark:bg-secondary-dark rounded-lg w-full max-w-xl mx-auto self-center shadow-2xl transform transition-all max-h-[85vh] flex flex-col"
          aria-labelledby="import-users-title"
        >
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-3">
                  <div className="w-16 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <HiOutlineUserGroup className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex flex-col">
                  <h3 id="import-users-title" className="text-lg font-semibold text-gray-900 dark:text-white">
                    Import {activeTab === IntegrationActiveType.AZURE ? "Azure" : "Jira"} People
                  </h3>

                  <p className="text-sm  text-gray-700 dark:text-gray-300">
                    You're about to import people into Cadence Pro based on your {activeTab === IntegrationActiveType.AZURE ? "Azure DevOps" : "Jira"} selections. {activeTab === IntegrationActiveType.AZURE ? "Azure DevOps" : "Jira"} will remain unchanged.
                  </p>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowImportConfirm(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <span className="sr-only">Close</span>
                <HiX className="h-5 w-5" />
              </button>
             
            </div>

          </div>

          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {(() => {
              const { added } = getChangedUsers();

              if (added.length === 0) {
                return (
                  <div className="text-center py-12">
                    <div className="text-gray-400 text-lg mb-2">No users selected</div>
                    <div className="text-gray-500 text-sm">Select at least one user to import</div>
                  </div>
                );
              }

              return (
                <div className="space-y-4">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Users to import ({added.length})
                  </div>
                  <ul className="rounded-md border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
                    {added.map((user: User) => (
                      <li key={`add-${user.id}`} className="px-3 py-2 flex items-center gap-3">
                        <div className="h-7 w-7 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                          <span className="text-gray-700 dark:text-gray-200 text-xs font-semibold">
                            {user.display_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-gray-900 dark:text-white text-sm font-medium truncate">
                            {user.display_name}
                          </p>
                          <p className="text-gray-600 dark:text-gray-400 text-xs truncate">
                            {(userEmails[user.id] && userEmails[user.id].trim() !== '') 
                              ? userEmails[user.id]
                              : (user.email || 'No email available')
                            }
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}
          </div>

          <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex space-x-3 justify-end items-center">
              <CustomButton
                type="button"
                variant="secondary"
                onClick={() => setShowImportConfirm(false)}
                 
              >
                Cancel
              </CustomButton>

              <CustomButton
                onClick={confirmImport}
                isLoading={importUsersLoading}
                disabled={!hasChangesToImport}
              >
                <span>Import</span>
              </CustomButton>
            </div>
          </div>
        </dialog>
      </div>
    </Portal>
  )
}

export default SyncUsersModal;